
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-hot-toast";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faAdd,
  faChevronLeft,
  faChevronRight,
  faEdit,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import {
  Modal,
  Box,
  Typography,
  Button,
} from "@mui/material";

import "../screenstyles/Product.css";
import "../screenstyles/CollectionManagement.css";

import { ApiError } from "../api/apiError";

import { categoriesApi } from "../api/categoriesApi";

import type {
  Category,
  CategoryTreeNode,
  CategoryType,
} from "../types/categories.types";

/*
 * ====================================
 * Types
 * ====================================
 */

type CategoryForm = {
  id: string;
  name: string;
  nameAr: string;
  type: CategoryType;
  parentId: string | null;
  level: 1 | 2 | 3;
  isActive: boolean;
  sortOrder: number;
};

type CursorHistoryItem = {
  page: number;
  cursor: string | undefined;
};

/*
 * ====================================
 * Constants
 * ====================================
 */

const DEFAULT_PAGE_SIZE = 50;

const PAGE_SIZE_OPTIONS = [
  25,
  50,
  100,
];

const emptyCategory: CategoryForm = {
  id: "",
  name: "",
  nameAr: "",
  type: "main",
  parentId: null,
  level: 1,
  isActive: true,
  sortOrder: 1,
};

/*
 * ====================================
 * Helpers
 * ====================================
 */

function slugify(
  value: string,
) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\+/g, "plus")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getCategoryErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (!(error instanceof ApiError)) {
    return fallbackMessage;
  }

  switch (error.code) {
    case "CATEGORY_ALREADY_EXISTS":
      return "يوجد تصنيف آخر بنفس الـ ID.";

    case "CATEGORY_NAME_ALREADY_EXISTS":
      return "يوجد تصنيف آخر بنفس الاسم الإنجليزي.";

    case "CATEGORY_NOT_FOUND":
      return "التصنيف غير موجود.";

    case "CATEGORY_PARENT_REQUIRED":
      return "يجب اختيار التصنيف الأب.";

    case "CATEGORY_PARENT_NOT_FOUND":
      return "التصنيف الأب غير موجود.";

    case "CATEGORY_PARENT_TYPE_INVALID":
      return "نوع التصنيف الأب غير مناسب للتصنيف المحدد.";

    case "CATEGORY_PARENT_INACTIVE":
      return "لا يمكن استخدام تصنيف أب غير مفعل.";

    case "MAIN_CATEGORY_PARENT_NOT_ALLOWED":
      return "التصنيف الرئيسي لا يمكن أن يحتوي على أب.";

    case "CATEGORY_SELF_PARENT_NOT_ALLOWED":
      return "لا يمكن أن يكون التصنيف أبًا لنفسه.";

    case "CATEGORY_CIRCULAR_PARENT":
      return "لا يمكن إنشاء تسلسل دائري بين التصنيفات.";

    case "CATEGORY_TYPE_CHANGE_HAS_CHILDREN":
      return "لا يمكن تغيير نوع التصنيف بسبب وجود تصنيفات فرعية مرتبطة به.";

    case "CATEGORY_HAS_CHILDREN":
      return "لا يمكن تعطيل تصنيف يحتوي على تصنيفات فرعية.";

    case "CATEGORY_ALREADY_INACTIVE":
      return "هذا التصنيف غير مفعل بالفعل.";

    case "FORBIDDEN":
      return "لا تملك الصلاحية المطلوبة لتنفيذ العملية.";

    case "VALIDATION_ERROR":
      return "بيانات التصنيف غير صحيحة. تحقق من الحقول.";

    default:
      return error.message || fallbackMessage;
  }
}

function getLevelByType(
  type: CategoryType,
): 1 | 2 | 3 {
  if (type === "main") {
    return 1;
  }

  if (type === "sub") {
    return 2;
  }

  return 3;
}

/*
 * نحول tree إلى array كاملة
 * حتى نستخدم كل التصنيفات
 * في Parent Select.
 */
function flattenCategoryTree(
  nodes: CategoryTreeNode[],
): Category[] {
  const result: Category[] = [];

  const walk = (
    items: CategoryTreeNode[],
  ) => {
    items.forEach(
      item => {
        const {
          children,
          ...category
        } = item;

        result.push(
          category,
        );

        if (
          children?.length
        ) {
          walk(
            children,
          );
        }
      },
    );
  };

  walk(
    nodes,
  );

  return result;
}

function getParentOptions(
  categories: Category[],
  type: CategoryType,
) {
  if (
    type === "main"
  ) {
    return [];
  }

  if (
    type === "sub"
  ) {
    return categories.filter(
      category =>
        category.type ===
          "main" &&
        category.isActive,
    );
  }

  return categories.filter(
    category =>
      category.type ===
        "sub" &&
      category.isActive,
  );
}

/*
 * ====================================
 * Component
 * ====================================
 */

const Categories: React.FC =
  () => {
    /*
     * الجدول الحالي فقط.
     */
    const [
      categories,
      setCategories,
    ] =
      useState<Category[]>(
        [],
      );

    /*
     * جميع التصنيفات.
     *
     * تستخدم فقط في:
     * - parent names
     * - parent select
     */
    const [
      allCategories,
      setAllCategories,
    ] =
      useState<Category[]>(
        [],
      );

    const [
      loading,
      setLoading,
    ] =
      useState(true);

    const [
      loadingTree,
      setLoadingTree,
    ] =
      useState(false);

    const [
      searchQuery,
      setSearchQuery,
    ] =
      useState("");

    /*
     * البحث الفعلي المرسل للـAPI.
     *
     * منفصل عن input
     * حتى نستخدم debounce.
     */
    const [
      appliedSearch,
      setAppliedSearch,
    ] =
      useState("");

    const [
      formOpen,
      setFormOpen,
    ] =
      useState(false);

    const [
      deleteOpen,
      setDeleteOpen,
    ] =
      useState(false);

    const [
      editingId,
      setEditingId,
    ] =
      useState<
        string | null
      >(null);

    const [
      selectedId,
      setSelectedId,
    ] =
      useState<
        string | null
      >(null);

    const [
      saving,
      setSaving,
    ] =
      useState(false);

    const [
      form,
      setForm,
    ] =
      useState<CategoryForm>(
        emptyCategory,
      );

    /*
     * ====================================
     * Pagination state
     * ====================================
     */

    const [
      pageSize,
      setPageSize,
    ] =
      useState(
        DEFAULT_PAGE_SIZE,
      );

    const [
      currentPage,
      setCurrentPage,
    ] =
      useState(1);

    /*
     * Cursor المستخدم
     * لتحميل الصفحة الحالية.
     *
     * الصفحة الأولى = undefined.
     */
    const [
      currentCursor,
      setCurrentCursor,
    ] =
      useState<
        string | undefined
      >(undefined);

    const [
      nextCursor,
      setNextCursor,
    ] =
      useState<
        string | null
      >(null);

    const [
      hasMore,
      setHasMore,
    ] =
      useState(false);

    /*
     * لأن Cursor Pagination
     * لا يعطينا previousCursor،
     * نخزن cursor كل صفحة
     * أثناء التنقل.
     */
    const [
      cursorHistory,
      setCursorHistory,
    ] =
      useState<
        CursorHistoryItem[]
      >([
        {
          page: 1,
          cursor: undefined,
        },
      ]);

    /*
     * ====================================
     * Fetch full tree
     * ====================================
     */

    const fetchAllCategories =
      async () => {
        try {
          setLoadingTree(
            true,
          );

          const result =
            await categoriesApi.tree();

          const flattened =
            flattenCategoryTree(
              result.items,
            );

          setAllCategories(
            flattened,
          );
        } catch (
          error
        ) {
          console.error(
            "Error fetching categories tree:",
            error,
          );
        } finally {
          setLoadingTree(
            false,
          );
        }
      };

    /*
     * ====================================
     * Fetch one page
     * ====================================
     */

    const fetchCategories =
      async (
        cursor:
          | string
          | undefined =
          currentCursor,

        options?: {
          silent?: boolean;
        },
      ) => {
        if (
          !options?.silent
        ) {
          setLoading(
            true,
          );
        }

        try {
          const result =
            await categoriesApi.list(
              {
                sort:
                  "level_asc",

                limit:
                  pageSize,

                cursor,

                search:
                  appliedSearch ||
                  undefined,
              },
            );

          setCategories(
            result.items,
          );

          setHasMore(
            Boolean(
              result
                .pagination
                .hasMore,
            ),
          );

          setNextCursor(
            result
              .pagination
              .nextCursor ??
              null,
          );
        } catch (
          error
        ) {
          console.error(
            "Error fetching categories from API:",
            error,
          );

          toast.error(
            "حدث خطأ أثناء تحميل التصنيفات من الخادم",
          );
        } finally {
          setLoading(
            false,
          );
        }
      };

    /*
     * ====================================
     * Initial tree
     * ====================================
     */

    useEffect(
      () => {
        fetchAllCategories();
      },
      [],
    );

    /*
     * ====================================
     * Search debounce
     * ====================================
     */

    useEffect(
      () => {
        const timeout =
          window.setTimeout(
            () => {
              setAppliedSearch(
                searchQuery.trim(),
              );
            },
            350,
          );

        return () => {
          window.clearTimeout(
            timeout,
          );
        };
      },
      [
        searchQuery,
      ],
    );

    /*
     * ====================================
     * Reset pagination when
     * search/pageSize changes
     * ====================================
     */

    useEffect(
      () => {
        setCurrentPage(
          1,
        );

        setCurrentCursor(
          undefined,
        );

        setCursorHistory([
          {
            page:
              1,

            cursor:
              undefined,
          },
        ]);
      },
      [
        appliedSearch,
        pageSize,
      ],
    );

    /*
     * ====================================
     * Fetch whenever pagination changes
     * ====================================
     */

    useEffect(
      () => {
        fetchCategories(
          currentCursor,
        );
      },
      [
        currentCursor,
        appliedSearch,
        pageSize,
      ],
    );

    /*
     * ====================================
     * Parent labels
     * ====================================
     */

    const parentNameById =
      useMemo(
        () => {
          return allCategories.reduce<
            Record<
              string,
              string
            >
          >(
            (
              acc,
              item,
            ) => {
              acc[
                item.id
              ] =
                item.nameAr ||
                item.name ||
                item.id;

              return acc;
            },
            {},
          );
        },
        [
          allCategories,
        ],
      );

    /*
     * البحث أصبح Server Side.
     *
     * لذلك لا نعيد filter هنا.
     */
    const visibleCategories =
      categories;

    /*
     * ====================================
     * Next page
     * ====================================
     */

    const handleNextPage =
      () => {
        if (
          !hasMore ||
          !nextCursor ||
          loading
        ) {
          return;
        }

        const nextPage =
          currentPage +
          1;

        setCursorHistory(
          previous => {
            const existing =
              previous.find(
                item =>
                  item.page ===
                  nextPage,
              );

            if (
              existing
            ) {
              return previous;
            }

            return [
              ...previous,

              {
                page:
                  nextPage,

                cursor:
                  nextCursor,
              },
            ];
          },
        );

        setCurrentPage(
          nextPage,
        );

        setCurrentCursor(
          nextCursor,
        );
      };

    /*
     * ====================================
     * Previous page
     * ====================================
     */

    const handlePreviousPage =
      () => {
        if (
          currentPage <=
            1 ||
          loading
        ) {
          return;
        }

        const previousPage =
          currentPage -
          1;

        const historyItem =
          cursorHistory.find(
            item =>
              item.page ===
              previousPage,
          );

        setCurrentPage(
          previousPage,
        );

        setCurrentCursor(
          historyItem
            ?.cursor,
        );
      };

    /*
     * ====================================
     * Change page size
     * ====================================
     */

    const handlePageSizeChange =
      (
        value: number,
      ) => {
        setPageSize(
          value,
        );
      };

    /*
     * ====================================
     * Forms
     * ====================================
     */

    const openAdd =
      (
        type:
          CategoryType =
          "main",

        parentId:
          | string
          | null =
          null,
      ) => {
        setEditingId(
          null,
        );

        const sameTypeCount =
          allCategories.filter(
            category =>
              category.type ===
              type,
          ).length;

        setForm({
          ...emptyCategory,

          type,

          parentId:
            type ===
            "main"
              ? null
              : parentId,

          level:
            getLevelByType(
              type,
            ),

          sortOrder:
            sameTypeCount +
            1,
        });

        setFormOpen(
          true,
        );
      };

    const openEdit =
      (
        category: Category,
      ) => {
        setEditingId(
          category.id,
        );

        setForm({
          id:
            category.id,

          name:
            category.name,

          nameAr:
            category.nameAr,

          type:
            category.type,

          parentId:
            category.parentId,

          level:
            category.level,

          isActive:
            category.isActive,

          sortOrder:
            category.sortOrder,
        });

        setFormOpen(
          true,
        );
      };

    const closeForm =
      () => {
        setFormOpen(
          false,
        );

        setEditingId(
          null,
        );

        setForm(
          emptyCategory,
        );
      };

    const handleTypeChange =
      (
        type: CategoryType,
      ) => {
        setForm(
          current => ({
            ...current,

            type,

            level:
              getLevelByType(
                type,
              ),

            parentId:
              type ===
              "main"
                ? null
                : "",
          }),
        );
      };

    /*
     * ====================================
     * Refresh current data
     * ====================================
     */

    const refreshCategories =
      async () => {
        await Promise.all([
          fetchCategories(
            currentCursor,
            {
              silent:
                true,
            },
          ),

          fetchAllCategories(),
        ]);
      };

    /*
     * ====================================
     * Save
     * ====================================
     */

    const handleSave =
      async () => {
        const normalizedId =
          editingId ||
          slugify(
            form.id ||
              form.name,
          );

        const normalizedName =
          form.name.trim();

        const normalizedNameAr =
          form.nameAr.trim();

        const normalizedSortOrder =
          Number(
            form.sortOrder,
          );

        if (
          !normalizedId ||
          !normalizedName ||
          !normalizedNameAr
        ) {
          toast.error(
            "يرجى إدخال ID والاسم العربي والإنجليزي",
          );

          return;
        }

        if (
          form.type !==
            "main" &&
          !form.parentId
        ) {
          toast.error(
            "يرجى اختيار التصنيف الأب",
          );

          return;
        }

        if (
          !Number.isInteger(
            normalizedSortOrder,
          ) ||
          normalizedSortOrder <
            0
        ) {
          toast.error(
            "الترتيب يجب أن يكون عددًا صحيحًا يساوي صفرًا أو أكثر",
          );

          return;
        }

        const parentId =
          form.type ===
          "main"
            ? null
            : form.parentId;

        try {
          setSaving(
            true,
          );

          if (
            editingId
          ) {
            await categoriesApi.update(
              editingId,
              {
                name:
                  normalizedName,

                nameAr:
                  normalizedNameAr,

                type:
                  form.type,

                parentId,

                isActive:
                  Boolean(
                    form.isActive,
                  ),

                sortOrder:
                  normalizedSortOrder,
              },
            );
          } else {
            await categoriesApi.create(
              {
                id:
                  normalizedId,

                name:
                  normalizedName,

                nameAr:
                  normalizedNameAr,

                type:
                  form.type,

                parentId,

                isActive:
                  Boolean(
                    form.isActive,
                  ),

                sortOrder:
                  normalizedSortOrder,
              },
            );
          }

          toast.success(
            editingId
              ? "تم تعديل التصنيف"
              : "تمت إضافة التصنيف",
            {
              className:
                "products-toast-success",
            },
          );

          closeForm();

          await refreshCategories();
        } catch (
          error
        ) {
          console.error(
            "Error saving category through API:",
            error,
          );

          toast.error(
            getCategoryErrorMessage(
              error,
              "حدث خطأ أثناء حفظ التصنيف",
            ),
          );
        } finally {
          setSaving(
            false,
          );
        }
      };

    /*
     * ====================================
     * Delete
     * ====================================
     */

    const openDelete =
      (
        id: string,
      ) => {
        setSelectedId(
          id,
        );

        setDeleteOpen(
          true,
        );
      };

    const closeDelete =
      () => {
        setSelectedId(
          null,
        );

        setDeleteOpen(
          false,
        );
      };

    const handleDelete =
      async () => {
        if (
          !selectedId
        ) {
          return;
        }

        try {
          setSaving(
            true,
          );

          const result =
            await categoriesApi.delete(
              selectedId,
            );

          toast.success(
            result.productsCount >
              0
              ? `تم تعطيل التصنيف، وهو مرتبط بـ ${result.productsCount} منتج`
              : "تم تعطيل التصنيف",
            {
              className:
                "products-toast-success",
            },
          );

          closeDelete();

          await refreshCategories();
        } catch (
          error
        ) {
          console.error(
            "Error disabling category through API:",
            error,
          );

          toast.error(
            getCategoryErrorMessage(
              error,
              "حدث خطأ أثناء تعطيل التصنيف",
            ),
          );
        } finally {
          setSaving(
            false,
          );
        }
      };

    /*
     * ====================================
     * Parent options
     * ====================================
     */

    const parentOptions =
      getParentOptions(
        allCategories,
        form.type,
      );

    /*
     * ====================================
     * Render
     * ====================================
     */

    return (
      <div className="products-page collection-page collection-categories-page">
        {/* Header */}

        <div className="products-header collection-header collection-header-categories">
          <h1 className="products-title collection-title">
            إدارة التصنيفات
          </h1>

          <div className="products-search-box">
            <FontAwesomeIcon
              icon={
                faSearch
              }
              className="products-search-icon"
            />

            <input
              type="text"
              placeholder="البحث في جميع التصنيفات..."
              className="products-search-input"
              value={
                searchQuery
              }
              onChange={
                event =>
                  setSearchQuery(
                    event
                      .target
                      .value,
                  )
              }
            />
          </div>

          <button
            type="button"
            className="products-header-icon-btn"
            onClick={() =>
              openAdd(
                "main",
              )
            }
            title="إضافة تصنيف رئيسي"
          >
            <FontAwesomeIcon
              icon={
                faAdd
              }
            />
          </button>
        </div>

        {/* Table */}

        {loading ? (
          <div className="products-loading">
            Loading...
          </div>
        ) : (
          <>
            <div className="products-table-container">
              <table className="products-table collection-table">
                <thead>
                  <tr>
                    <th>
                      #
                    </th>

                    <th>
                      ID
                    </th>

                    <th>
                      الاسم EN
                    </th>

                    <th>
                      الاسم AR
                    </th>

                    <th>
                      النوع
                    </th>

                    <th>
                      المستوى
                    </th>

                    <th>
                      الأب
                    </th>

                    <th>
                      الحالة
                    </th>

                    <th>
                      الترتيب
                    </th>

                    <th>
                      إضافة فرعي
                    </th>

                    <th>
                      الإجراءات
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {visibleCategories.map(
                    (
                      category,
                      index,
                    ) => (
                      <tr
                        key={
                          category.id
                        }
                      >
                        <td>
                          {(currentPage -
                            1) *
                            pageSize +
                            index +
                            1}
                        </td>

                        <td className="products-cell-text">
                          {
                            category.id
                          }
                        </td>

                        <td className="products-cell-text">
                          {
                            category.name
                          }
                        </td>

                        <td
                          className="products-cell-text collection-tree-name"
                          style={{
                            paddingRight:
                              category.level *
                              18,
                          }}
                        >
                          {category.level ===
                          2
                            ? "↳ "
                            : category.level ===
                                3
                              ? "↳ ↳ "
                              : ""}

                          {
                            category.nameAr
                          }
                        </td>

                        <td className="products-cell-text">
                          {
                            category.type
                          }
                        </td>

                        <td className="products-cell-text">
                          {
                            category.level
                          }
                        </td>

                        <td className="products-cell-text">
                          {category.parentId
                            ? parentNameById[
                                category
                                  .parentId
                              ] ||
                              category.parentId
                            : "—"}
                        </td>

                        <td className="products-cell-text">
                          <span
                            className={
                              category.isActive
                                ? "collection-badge active"
                                : "collection-badge inactive"
                            }
                          >
                            {category.isActive
                              ? "مفعل"
                              : "غير مفعل"}
                          </span>
                        </td>

                        <td className="products-cell-text">
                          {
                            category.sortOrder
                          }
                        </td>

                        <td>
                          {category.type !==
                          "leaf" ? (
                            <button
                              type="button"
                              className="collection-mini-btn"
                              onClick={() =>
                                openAdd(
                                  category.type ===
                                    "main"
                                    ? "sub"
                                    : "leaf",

                                  category.id,
                                )
                              }
                            >
                              +{" "}
                              {category.type ===
                              "main"
                                ? "Sub"
                                : "Leaf"}
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>

                        <td>
                          <div className="products-actions">
                            <button
                              type="button"
                              className="products-action-btn products-edit-btn"
                              onClick={() =>
                                openEdit(
                                  category,
                                )
                              }
                              title="تعديل"
                            >
                              <FontAwesomeIcon
                                icon={
                                  faEdit
                                }
                              />
                            </button>

                            <button
                              type="button"
                              className="products-action-btn products-delete-btn"
                              onClick={() =>
                                openDelete(
                                  category.id,
                                )
                              }
                              title="تعطيل"
                            >
                              <FontAwesomeIcon
                                icon={
                                  faTrash
                                }
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>

              {visibleCategories.length ===
                0 && (
                <div className="products-empty-state">
                  لا توجد تصنيفات مطابقة للبحث.
                </div>
              )}
            </div>

            {/* Pagination */}

            <div
              style={{
                display:
                  "flex",

                alignItems:
                  "center",

                justifyContent:
                  "space-between",

                gap:
                  "16px",

                flexWrap:
                  "wrap",

                padding:
                  "16px 4px",
              }}
            >
              {/* Page size */}

              <div
                style={{
                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    "8px",
                }}
              >
                <span>
                  عدد العناصر:
                </span>

                <select
                  value={
                    pageSize
                  }
                  onChange={
                    event =>
                      handlePageSizeChange(
                        Number(
                          event
                            .target
                            .value,
                        ),
                      )
                  }
                  disabled={
                    loading
                  }
                  style={{
                    padding:
                      "7px 10px",

                    border:
                      "1px solid #d0d5dd",

                    borderRadius:
                      "8px",

                    background:
                      "#fff",
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map(
                    size => (
                      <option
                        key={
                          size
                        }
                        value={
                          size
                        }
                      >
                        {
                          size
                        }
                      </option>
                    ),
                  )}
                </select>
              </div>

              {/* Current page */}

              <div>
                الصفحة{" "}
                <strong>
                  {
                    currentPage
                  }
                </strong>

                {appliedSearch && (
                  <span>
                    {" "}
                    — نتائج البحث عن "
                    {
                      appliedSearch
                    }
                    "
                  </span>
                )}
              </div>

              {/* Controls */}

              <div
                style={{
                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    "8px",
                }}
              >
                <button
                  type="button"
                  className="collection-mini-btn"
                  onClick={
                    handlePreviousPage
                  }
                  disabled={
                    loading ||
                    currentPage <=
                      1
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      faChevronRight
                    }
                  />

                  {" "}
                  السابق
                </button>

                <button
                  type="button"
                  className="collection-mini-btn"
                  onClick={
                    handleNextPage
                  }
                  disabled={
                    loading ||
                    !hasMore ||
                    !nextCursor
                  }
                >
                  التالي
                  {" "}

                  <FontAwesomeIcon
                    icon={
                      faChevronLeft
                    }
                  />
                </button>
              </div>
            </div>
          </>
        )}

        {/* Add / Edit Modal */}

        <Modal
          open={
            formOpen
          }
          onClose={
            closeForm
          }
          className="products-modal"
        >
          <Box className="products-modal-card collection-form-card">
            <Typography className="products-modal-title">
              {editingId
                ? "تعديل تصنيف"
                : "إضافة تصنيف"}
            </Typography>

            <div className="collection-form-grid">
              <label>
                النوع

                <select
                  value={
                    form.type
                  }
                  onChange={
                    event =>
                      handleTypeChange(
                        event
                          .target
                          .value as CategoryType,
                      )
                  }
                >
                  <option value="main">
                    Main
                  </option>

                  <option value="sub">
                    Sub
                  </option>

                  <option value="leaf">
                    Leaf
                  </option>
                </select>
              </label>

              {form.type !==
                "main" && (
                <label>
                  التصنيف الأب

                  <select
                    value={
                      form.parentId ||
                      ""
                    }
                    onChange={
                      event =>
                        setForm(
                          current => ({
                            ...current,

                            parentId:
                              event
                                .target
                                .value,
                          }),
                        )
                    }
                    disabled={
                      loadingTree
                    }
                  >
                    <option value="">
                      {loadingTree
                        ? "جاري تحميل التصنيفات..."
                        : "اختر التصنيف الأب"}
                    </option>

                    {parentOptions.map(
                      parent => (
                        <option
                          key={
                            parent.id
                          }
                          value={
                            parent.id
                          }
                        >
                          {parent.nameAr ||
                            parent.name}
                        </option>
                      ),
                    )}
                  </select>
                </label>
              )}

              <label>
                ID

                <input
                  disabled={
                    Boolean(
                      editingId,
                    )
                  }
                  value={
                    form.id
                  }
                  onChange={
                    event =>
                      setForm(
                        current => ({
                          ...current,

                          id:
                            slugify(
                              event
                                .target
                                .value,
                            ),
                        }),
                      )
                  }
                  placeholder="video_surveillance"
                />
              </label>

              <label>
                الاسم EN

                <input
                  value={
                    form.name
                  }
                  onChange={
                    event =>
                      setForm(
                        current => ({
                          ...current,

                          name:
                            event
                              .target
                              .value,

                          id:
                            editingId
                              ? current.id
                              : slugify(
                                  event
                                    .target
                                    .value,
                                ),
                        }),
                      )
                  }
                  placeholder="Video Surveillance"
                />
              </label>

              <label>
                الاسم AR

                <input
                  value={
                    form.nameAr
                  }
                  onChange={
                    event =>
                      setForm(
                        current => ({
                          ...current,

                          nameAr:
                            event
                              .target
                              .value,
                        }),
                      )
                  }
                  placeholder="أنظمة المراقبة"
                />
              </label>

              <label>
                الترتيب

                <input
                  type="number"
                  value={
                    form.sortOrder
                  }
                  onChange={
                    event =>
                      setForm(
                        current => ({
                          ...current,

                          sortOrder:
                            Number(
                              event
                                .target
                                .value,
                            ),
                        }),
                      )
                  }
                />
              </label>

              <label className="collection-checkbox-label">
                <input
                  type="checkbox"
                  checked={
                    form.isActive
                  }
                  onChange={
                    event =>
                      setForm(
                        current => ({
                          ...current,

                          isActive:
                            event
                              .target
                              .checked,
                        }),
                      )
                  }
                />

                مفعل
              </label>
            </div>

            <Box className="products-modal-actions">
              <Button
                className="collection-save-btn"
                onClick={
                  handleSave
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? "جاري الحفظ..."
                  : "حفظ"}
              </Button>

              <Button
                className="products-modal-cancel-btn"
                onClick={
                  closeForm
                }
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete Modal */}

        <Modal
          open={
            deleteOpen
          }
          onClose={
            closeDelete
          }
          className="products-modal"
        >
          <Box className="products-modal-card">
            <Typography className="products-modal-title">
              هل أنت متأكد من تعطيل هذا التصنيف؟
            </Typography>

            <Box className="products-modal-actions">
              <Button
                className="products-modal-delete-btn"
                onClick={
                  handleDelete
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? "جاري التعطيل..."
                  : "نعم، عطّل"}
              </Button>

              <Button
                className="products-modal-cancel-btn"
                onClick={
                  closeDelete
                }
              >
                إلغاء
              </Button>
            </Box>
          </Box>
        </Modal>
      </div>
    );
  };

export default Categories;
